import { z } from 'zod';
import { AuditSchema, DateOnlySchema, IdSchema, InstantSchema } from './common.ts';
import { MoneySchema, addMoney, money, subtractMoney, type Money } from '../money.ts';
import type { Invoice } from './invoice.ts';
import { invoiceTotals } from './invoice.ts';

/**
 * Credit memos and deposits (P5-13).
 *
 * These are the two instruments the rest of this phase has been pointing
 * at. Three separate refusals — unreleasing a paid invoice, voiding one,
 * voiding a paid bill — all say "raise a credit memo instead", and until
 * now that was advice about something that did not exist.
 *
 * `docs/features/09-owner-invoices-payments.md` §3 describes both, and the
 * header strip in §1 is the sum they feed:
 *
 *     running total − applied credit memos − applied deposits − paid
 *       = remaining balance
 *
 * ## Why these are two records and not one
 *
 * They point in opposite directions and arrive at different times. A
 * **deposit** is money the owner has already handed over, before there was
 * an invoice to put it against; a **credit memo** is money the builder
 * agrees to take off, usually after an invoice turned out to be wrong. One
 * is cash received early, the other is a price reduced late, and a single
 * "adjustment" record carrying a sign would make every report have to
 * explain which kind each row was.
 */

/**
 * Where a credit memo's money went.
 *
 * `held` is the state §3 calls "hold on account": agreed but not yet put
 * against anything. It matters because an owner reading their balance needs
 * to see it — a credit sitting unapplied is money they are owed, and a
 * record with nowhere to show would be a quiet favour to the builder.
 */
export const CREDIT_STATES = ['held', 'applied', 'void'] as const;
export const CreditStateSchema = z.enum(CREDIT_STATES);
export type CreditState = z.infer<typeof CreditStateSchema>;

export const CreditMemoSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  /** `CM-0001`: its own sequence, because it is its own document. */
  number: z.string().trim().min(1).max(32),
  amount: MoneySchema,
  /**
   * Why it was given, and **required**.
   *
   * A credit with no reason is a figure nobody can defend in six months,
   * and this is the record most likely to be read back in an argument.
   * Every other description in this phase is optional; this one is not.
   */
  reason: z.string().trim().min(1).max(2_000),
  state: CreditStateSchema.default('held'),
  /** The invoice it was applied to, once it is. */
  appliedToInvoiceId: IdSchema.optional(),
  appliedAt: InstantSchema.optional(),
});
export type CreditMemo = z.infer<typeof CreditMemoSchema>;

export const DepositSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  number: z.string().trim().min(1).max(32),
  amount: MoneySchema,
  /** When the money actually arrived, which is not when somebody filed it. */
  receivedAt: DateOnlySchema,
  method: z.enum(['check', 'card', 'ach', 'cash', 'other']).default('check'),
  reference: z.string().trim().max(120).optional(),
  note: z.string().trim().max(2_000).optional(),
  /**
   * The invoices it has been applied to, and how much to each.
   *
   * A list rather than one id, because a deposit outlives the first
   * invoice it touches: $5,000 taken up front is applied $2,000 here and
   * $3,000 there as the job runs. Each entry is an amount, so
   * `depositRemaining` is a subtraction rather than a guess.
   */
  applications: z
    .array(z.object({ invoiceId: IdSchema, amount: MoneySchema, at: InstantSchema }))
    .default([]),
});
export type Deposit = z.infer<typeof DepositSchema>;

/** A deposit's unapplied balance: what is still available to put somewhere. */
export function depositRemaining(deposit: Pick<Deposit, 'amount' | 'applications'>): Money {
  const used = deposit.applications.reduce(
    (running, entry) => addMoney(running, entry.amount),
    money(0, deposit.amount.currency),
  );
  return subtractMoney(deposit.amount, used);
}

/** Whether any of a deposit is left to apply. */
/**
 * The applications left once an invoice no longer deserves any
 * (SCRUM-281).
 *
 * **Voiding an invoice releases the deposit rather than refusing the
 * void.** The two are not the same as a cash payment, which does block
 * a void: cash actually changed hands and hiding it behind a void would
 * be a lie. A deposit is money the builder already holds, and voiding
 * only moves the earmark — so the right answer is to hand it back to
 * the pool and let it be applied to a new invoice.
 *
 * Reported from dev on 2026-10-29 with the money stranded in real data:
 * $500 applied to an invoice that had been voided, counted as spent by
 * `depositRemaining` and applicable to nothing.
 */
export function applicationsWithout(
  deposit: Pick<Deposit, 'applications'>,
  invoiceId: string,
): Deposit['applications'] {
  return deposit.applications.filter((entry) => entry.invoiceId !== invoiceId);
}

/** Whether this credit memo is currently applied to that invoice. */
export function creditIsAppliedTo(
  credit: Pick<CreditMemo, 'state' | 'appliedToInvoiceId'>,
  invoiceId: string,
): boolean {
  return credit.state === 'applied' && credit.appliedToInvoiceId === invoiceId;
}

export function depositIsSpent(deposit: Pick<Deposit, 'amount' | 'applications'>): boolean {
  return depositRemaining(deposit).amount <= 0;
}

/**
 * What a credit memo may do next.
 *
 * `applied → held` is **not** allowed: taking a credit back off an invoice
 * the owner has seen would change a document they have been given. Voiding
 * the memo and raising another is the honest path, and leaves both facts on
 * the record.
 */
export const CREDIT_TRANSITIONS: Readonly<Record<CreditState, readonly CreditState[]>> = {
  held: ['applied', 'void'],
  applied: ['void'],
  void: [],
};

export function canTransitionCredit(from: CreditState, to: CreditState): boolean {
  return CREDIT_TRANSITIONS[from].includes(to);
}

export interface InvoiceSettlement {
  /** The invoice's own total, before anything is taken off. */
  readonly total: Money;
  readonly credits: Money;
  readonly deposits: Money;
  readonly paid: Money;
  /** Total less credits, deposits and payments. Negative means overpaid. */
  readonly balance: Money;
}

/**
 * What one invoice still owes, with credits and deposits taken off.
 *
 * The sum from §1, per invoice. **Order matters to nobody here** — it is
 * subtraction — but which records are included does: only `applied`
 * credits and only the applications naming this invoice, because a credit
 * held on account has not reduced anything yet and a deposit applied
 * elsewhere is already spent.
 *
 * `invoiceTotals` already takes the payments off; this takes the other two
 * off as well, and the two functions are kept apart so the invoice screen
 * can show "total less paid" without pretending to know about credits it
 * has not loaded.
 */
export function settleInvoice(
  invoice: Invoice,
  credits: readonly CreditMemo[],
  deposits: readonly Deposit[],
  currency = 'USD',
): InvoiceSettlement {
  const totals = invoiceTotals(invoice, currency);
  const zero = money(0, currency);

  const creditTotal = credits
    .filter((credit) => credit.state === 'applied' && credit.appliedToInvoiceId === invoice.id)
    .reduce((running, credit) => addMoney(running, credit.amount), zero);

  const depositTotal = deposits
    .flatMap((deposit) => deposit.applications)
    .filter((entry) => entry.invoiceId === invoice.id)
    .reduce((running, entry) => addMoney(running, entry.amount), zero);

  return {
    total: totals.total,
    credits: creditTotal,
    deposits: depositTotal,
    paid: totals.paid,
    balance: subtractMoney(subtractMoney(totals.balance, creditTotal), depositTotal),
  };
}

/**
 * The job's receivables header from §1.
 *
 * Deliberately **not** "the sum of every invoice's balance": a credit held
 * on account and a deposit not yet applied belong in this figure too, and
 * an owner whose unapplied deposit went unmentioned would be reading a
 * larger number than they owe. So held credits and unapplied deposits
 * reduce the total here while reducing no individual invoice.
 */
export interface Receivables {
  readonly invoiced: Money;
  readonly credits: Money;
  readonly deposits: Money;
  readonly paid: Money;
  readonly remaining: Money;
}

export function receivables(
  invoices: readonly Invoice[],
  credits: readonly CreditMemo[],
  deposits: readonly Deposit[],
  currency = 'USD',
): Receivables {
  const zero = money(0, currency);
  const released = invoices.filter((invoice) => invoice.status === 'released');

  const invoiced = released.reduce(
    (running, invoice) => addMoney(running, invoiceTotals(invoice, currency).total),
    zero,
  );
  const paid = released.reduce(
    (running, invoice) => addMoney(running, invoiceTotals(invoice, currency).paid),
    zero,
  );
  // Every credit that is not void counts, applied or held: both are money
  // the owner does not owe.
  const creditTotal = credits
    .filter((credit) => credit.state !== 'void')
    .reduce((running, credit) => addMoney(running, credit.amount), zero);
  const depositTotal = deposits.reduce(
    (running, deposit) => addMoney(running, deposit.amount),
    zero,
  );

  return {
    invoiced,
    credits: creditTotal,
    deposits: depositTotal,
    paid,
    remaining: subtractMoney(
      subtractMoney(subtractMoney(invoiced, creditTotal), depositTotal),
      paid,
    ),
  };
}

/** `CM-0001`, `DEP-0001` — their own sequences, zero-padded to sort. */
export function formatCreditNumber(sequence: number): string {
  return `CM-${String(sequence).padStart(4, '0')}`;
}

export function formatDepositNumber(sequence: number): string {
  return `DEP-${String(sequence).padStart(4, '0')}`;
}

export function parseLedgerNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  const tail = trimmed.slice(trimmed.lastIndexOf('-') + 1);
  if (!/^\d{1,4}$/.test(tail)) return undefined;
  const sequence = Number.parseInt(tail, 10);
  return sequence >= 1 && sequence <= 9999 ? sequence : undefined;
}
